import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DiseaseGroupFilter } from '../components/DiseaseGroupFilter';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { errText } from '../utils/errors';
import { MagnifyingGlass, UsersFour, Funnel, X, ArrowsClockwise, ListPlus, DeviceMobile, FileXls, ChartBar, CaretDown, CaretUp, FloppyDisk, UsersThree, Trash, Plus } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OPTIONS = {
  genders: ['Femenino', 'Masculino', 'Otro'],
  ethnicities: ['Caucásica', 'Negra', 'Latina', 'Asiática', 'Mixta'],
  skin_types: ['Grasa', 'Mixta', 'Normal', 'Seca', 'Sensible'],
  pigmented_skin: ['Lentigos', 'Melasma'],
  phototypes: ['I', 'II', 'III', 'IV', 'V', 'VI'],
  skin_alterations: ['Acné', 'Cuperosis', 'Dermatitis atópica', 'Dermatitis seborreica', 'Psoriasis', 'Rosacea', 'Rosacea ocular', 'Urticaria'],
  body_aspects: ['Arañas', 'Celulitis', 'Estrías blancas', 'Estrías rojas', 'Foliculitis', 'Queratosis pilar', 'Varices'],
  hair_types: ['Corto', 'Graso', 'Largo', 'Liso', 'Normal', 'Ondulado', 'Rizado', 'Seco'],
  scalp_alterations: ['Alopecia androgénica', 'Alopecia areata', 'Caspa', 'Efluvio telógeno', 'Hongos', 'Piojos', 'Psoriasis'],
  dental_alterations: ['Aftas orales', 'Boca seca', 'Encías sensibles', 'Herpes labial'],
  skin_reactivity: ['Normal', 'Sensible'],
};

const LABELS_ES = {
  title: 'Reclutamiento',
  search: 'Buscar',
  reset: 'Limpiar',
  results: 'Resultados',
  noResults: 'No se encontraron voluntarios con estos criterios',
  age: 'Edad',
  from: 'Desde',
  to: 'Hasta',
  gender: 'Genero',
  ethnicity: 'Etnia',
  skinType: 'Tipo de piel',
  pigmentedSkin: 'Piel pigmentada',
  phototype: 'Fototipo',
  skinAlterations: 'Alteraciones piel',
  skinReactivity: 'Reactividad de la piel',
  bodyAspects: 'Aspectos corporales',
  hairType: 'Tipo cabello',
  scalpAlterations: 'Alteraciones cuero cabelludo',
  dentalAlterations: 'Alteraciones dentales',
  personalData: 'Datos personales',
  qsalud: 'Qsalud',
  staffOnly: 'Personal de la casa',
  noActiveStudies: 'Sin estudios activos',
  id: 'ID', name: 'Nombre', email: 'Email', phone: 'Telefono',
  all: 'Todos (AND)', any: 'Alguno (OR)',
  volunteersFound: 'voluntarios encontrados',
  searching: 'Buscando...',
  generateList: 'Generar Lista',
  generateListTitle: 'Crear lista en Acumbamail',
  generateListDesc: 'Se creara una lista con los voluntarios del resultado que tengan email valido',
  listName: 'Nombre de la lista',
  creating: 'Creando...',
  listCreated: 'Lista creada',
  subscribersAdded: 'suscriptores anadidos',
  skippedNoEmail: 'omitidos sin email',
  sendSms: 'Enviar SMS',
  generateExcel: 'Generar Excel',
  sendSmsTitle: 'Enviar SMS a voluntarios',
  sendSmsDesc: 'Se enviara un SMS a los voluntarios del resultado que tengan telefono valido',
  smsBody: 'Mensaje SMS',
  smsSent: 'SMS enviados',
  skippedNoPhone: 'omitidos sin telefono',
  saveSegment: 'Guardar segmento',
  segments: 'Segmentos',
  saveSegmentTitle: 'Guardar como segmento',
  saveSegmentDesc: 'Guarda los criterios actuales como un segmento reutilizable. Es dinamico: se recalcula siempre, asi que incluye a los voluntarios que cumplan en cada momento. Sirve para dirigir Nuevos Estudios o anuncios a este publico en la App.',
  segmentName: 'Nombre del segmento',
  segmentDesc: 'Descripcion (opcional)',
  segmentSaved: 'Segmento guardado',
  segmentDeleted: 'Segmento eliminado',
  segmentsTitle: 'Segmentos guardados',
  noSegments: 'No hay segmentos guardados todavia.',
  save: 'Guardar',
  deleteConfirm: 'Eliminar este segmento?',
  useSegment: 'Usar',
  segmentLoaded: 'Segmento cargado en los filtros',
  segmentUpdated: 'Segmento actualizado',
  updateSegment: 'Actualizar segmento',
  saveAsNew: 'Guardar como nuevo',
  editingSegment: 'Editando segmento',
};

const LABELS_EN = {
  title: 'Recruitment',
  search: 'Search',
  reset: 'Reset',
  results: 'Results',
  noResults: 'No volunteers found matching these criteria',
  age: 'Age',
  from: 'From',
  to: 'To',
  gender: 'Gender',
  ethnicity: 'Ethnicity',
  skinType: 'Skin type',
  pigmentedSkin: 'Pigmented skin',
  phototype: 'Phototype',
  skinAlterations: 'Skin alterations',
  skinReactivity: 'Skin reactivity',
  bodyAspects: 'Body aspects',
  hairType: 'Hair type',
  scalpAlterations: 'Scalp alterations',
  dentalAlterations: 'Dental alterations',
  personalData: 'Personal data',
  qsalud: 'Qsalud',
  staffOnly: 'In-house staff',
  noActiveStudies: 'No active studies',
  id: 'ID', name: 'Name', email: 'Email', phone: 'Phone',
  all: 'All (AND)', any: 'Any (OR)',
  volunteersFound: 'volunteers found',
  searching: 'Searching...',
  generateList: 'Generate List',
  generateListTitle: 'Create list in Acumbamail',
  generateListDesc: 'A list will be created with the volunteers from the results that have a valid email',
  listName: 'List name',
  creating: 'Creating...',
  listCreated: 'List created',
  subscribersAdded: 'subscribers added',
  skippedNoEmail: 'skipped (no email)',
  sendSms: 'Send SMS',
  generateExcel: 'Generate Excel',
  sendSmsTitle: 'Send SMS to volunteers',
  sendSmsDesc: 'An SMS will be sent to volunteers from the results that have a valid phone',
  smsBody: 'SMS message',
  smsSent: 'SMS sent',
  skippedNoPhone: 'skipped (no phone)',
  saveSegment: 'Save segment',
  segments: 'Segments',
  saveSegmentTitle: 'Save as segment',
  saveSegmentDesc: 'Save the current criteria as a reusable segment. It is dynamic: it recomputes every time, so it includes whoever matches at any given moment. Use it to target Upcoming Studies or ads to this audience in the App.',
  segmentName: 'Segment name',
  segmentDesc: 'Description (optional)',
  segmentSaved: 'Segment saved',
  segmentDeleted: 'Segment deleted',
  segmentsTitle: 'Saved segments',
  noSegments: 'No saved segments yet.',
  save: 'Save',
  deleteConfirm: 'Delete this segment?',
  useSegment: 'Use',
  segmentLoaded: 'Segment loaded into filters',
  segmentUpdated: 'Segment updated',
  updateSegment: 'Update segment',
  saveAsNew: 'Save as new',
  editingSegment: 'Editing segment',
};

const INITIAL_FILTERS = {
  age_from: '', age_to: '',
  genders: [], ethnicities: [],
  phototypes: [],  // legacy: fototipo Fitzpatrick (numeros romanos, se preserva por retrocompat)
  staff_only: false,
  no_active_studies: false,
  dg_include: [], dg_exclude: [],
};

// URS-F456: estado dinamico del Qsalud.
// - qsaludFilters[field_key]  = string[] (valores seleccionados en chips)
// - qsaludFiltersMode[fk]     = 'or'|'and'
// - qsaludText[field_key]     = string (busqueda libre en textareas)
const INITIAL_QSALUD_FILTERS = {};
const INITIAL_QSALUD_MODE = {};
const INITIAL_QSALUD_TEXT = {};

export default function Recruitment() {
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const lb = lang === 'es' ? LABELS_ES : LABELS_EN;
  const [filters, setFilters] = useState({ ...INITIAL_FILTERS });
  // URS-F456: campos dinamicos del Qsalud cargados desde el backend.
  const [qsaludChipFields, setQsaludChipFields] = useState([]);
  const [qsaludTextFields, setQsaludTextFields] = useState([]);
  const [qsaludFilters, setQsaludFilters] = useState({ ...INITIAL_QSALUD_FILTERS });
  const [qsaludMode, setQsaludMode] = useState({ ...INITIAL_QSALUD_MODE });
  const [qsaludText, setQsaludText] = useState({ ...INITIAL_QSALUD_TEXT });
  const [results, setResults] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [showGenerateList, setShowGenerateList] = useState(false);
  const [listName, setListName] = useState('');
  const [generatingList, setGeneratingList] = useState(false);
  const [genProgress, setGenProgress] = useState('');
  const [showSendSms, setShowSendSms] = useState(false);
  const [smsBody, setSmsBody] = useState('');
  const [sendingSms, setSendingSms] = useState(false);
  // URS-F480: Exportar a Excel el listado de reclutamiento.
  const [exportingExcel, setExportingExcel] = useState(false);
  // Segmentos / audiencias
  const [showSaveSegment, setShowSaveSegment] = useState(false);
  const [segmentName, setSegmentName] = useState('');
  const [segmentDescription, setSegmentDescription] = useState('');
  const [savingSegment, setSavingSegment] = useState(false);
  const [showSegments, setShowSegments] = useState(false);
  const [segmentsList, setSegmentsList] = useState([]);
  const [loadingSegments, setLoadingSegments] = useState(false);
  const [activeSegment, setActiveSegment] = useState(null);

  const validEmails = results ? [...new Map(results.filter(v => v.email && v.email.includes('@')).map(v => [v.email.toLowerCase(), v])).values()] : [];
  const numLists = Math.ceil(validEmails.length / 500) || 1;

  const toggleOption = (field, value) => {
    setFilters(p => {
      const arr = p[field];
      return { ...p, [field]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
    });
  };

  // URS-F456: toggle para chips dinamicos del Qsalud
  const toggleQsalud = (fieldKey, value) => {
    setQsaludFilters(p => {
      const arr = p[fieldKey] || [];
      return { ...p, [fieldKey]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
    });
  };

  // URS-F456: cargar campos dinamicos del Qsalud al montar
  useEffect(() => {
    axios.get(`${API_URL}/api/recruitment/qsalud-fields`, { withCredentials: true })
      .then(r => {
        setQsaludChipFields(r.data.chip_fields || []);
        setQsaludTextFields(r.data.text_fields || []);
      })
      .catch(() => { /* no critico: la seccion queda vacia */ });
  }, []);

  // URS-F480: extraido a helper para poder reutilizar en handleExportExcel.
  const buildSearchPayload = () => {
    const payload = {};
    if (filters.age_from) payload.age_from = parseInt(filters.age_from);
    if (filters.age_to) payload.age_to = parseInt(filters.age_to);
    if (filters.genders.length) payload.genders = filters.genders;
    if (filters.ethnicities.length) payload.ethnicities = filters.ethnicities;
    if (filters.phototypes.length) payload.phototypes = filters.phototypes;
    if (filters.staff_only) payload.staff_only = true;
    if (filters.no_active_studies) payload.no_active_studies = true;
    if (filters.dg_include.length) payload.disease_groups_include = filters.dg_include;
    if (filters.dg_exclude.length) payload.disease_groups_exclude = filters.dg_exclude;
    const qFilters = {};
    Object.entries(qsaludFilters).forEach(([fk, values]) => {
      if (Array.isArray(values) && values.length > 0) {
        qFilters[fk] = { values, mode: qsaludMode[fk] || 'or' };
      }
    });
    if (Object.keys(qFilters).length > 0) payload.qsalud_filters = qFilters;
    const qText = {};
    Object.entries(qsaludText).forEach(([fk, term]) => {
      if (typeof term === 'string' && term.trim()) qText[fk] = term.trim();
    });
    if (Object.keys(qText).length > 0) payload.qsalud_text_search = qText;
    return payload;
  };

  const handleSearch = async () => {
    setLoading(true);
    setSearched(true);
    try {
      const r = await axios.post(`${API_URL}/api/recruitment/search`, buildSearchPayload(), { withCredentials: true });
      setResults(r.data.data || []);
      setTotal(r.data.total || 0);
    } catch (err) {
      toast.error(errText(err));
    }
    setLoading(false);
  };

  const handleExportExcel = async () => {
    setExportingExcel(true);
    try {
      const r = await axios.post(`${API_URL}/api/recruitment/export.xlsx`, buildSearchPayload(), {
        withCredentials: true,
        responseType: 'blob',
      });
      const blob = new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '').replace(/-/g, '');
      a.download = `reclutamiento_${ts}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(lang === 'es' ? 'Excel generado' : 'Excel generated');
    } catch (err) {
      toast.error(errText(err));
    }
    setExportingExcel(false);
  };

  const handleSaveSegment = async () => {
    if (!segmentName.trim()) { toast.error(lb.segmentName); return; }
    setSavingSegment(true);
    try {
      await axios.post(`${API_URL}/api/recruitment/segments`, {
        name: segmentName.trim(),
        description: segmentDescription.trim(),
        criteria: buildSearchPayload(),
      }, { withCredentials: true });
      toast.success(lb.segmentSaved);
      setShowSaveSegment(false);
      setSegmentName('');
      setSegmentDescription('');
    } catch (err) {
      toast.error(errText(err));
    }
    setSavingSegment(false);
  };

  const handleUpdateSegment = async () => {
    if (!activeSegment) return;
    if (!segmentName.trim()) { toast.error(lb.segmentName); return; }
    setSavingSegment(true);
    try {
      await axios.put(`${API_URL}/api/recruitment/segments/${activeSegment.id}`, {
        name: segmentName.trim(),
        description: segmentDescription.trim(),
        criteria: buildSearchPayload(),
      }, { withCredentials: true });
      toast.success(lb.segmentUpdated);
      setActiveSegment({ ...activeSegment, name: segmentName.trim(), description: segmentDescription.trim() });
      setShowSaveSegment(false);
    } catch (err) {
      toast.error(errText(err));
    }
    setSavingSegment(false);
  };

  const openSaveSegment = () => {
    if (activeSegment) {
      setSegmentName(activeSegment.name || '');
      setSegmentDescription(activeSegment.description || '');
    } else {
      setSegmentName('');
      setSegmentDescription('');
    }
    setShowSaveSegment(true);
  };

  const loadSegments = async () => {
    setLoadingSegments(true);
    try {
      const r = await axios.get(`${API_URL}/api/recruitment/segments`, { withCredentials: true });
      setSegmentsList(r.data || []);
    } catch (err) {
      toast.error(errText(err));
    }
    setLoadingSegments(false);
  };

  const openSegments = () => { setShowSegments(true); loadSegments(); };

  const handleDeleteSegment = async (id) => {
    if (!window.confirm(lb.deleteConfirm)) return;
    try {
      await axios.delete(`${API_URL}/api/recruitment/segments/${id}`, { withCredentials: true });
      setSegmentsList((prev) => prev.filter((s) => s.id !== id));
      toast.success(lb.segmentDeleted);
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const loadSegment = async (seg) => {
    const c = seg.criteria || {};
    setFilters({
      ...INITIAL_FILTERS,
      age_from: c.age_from != null ? String(c.age_from) : '',
      age_to: c.age_to != null ? String(c.age_to) : '',
      genders: c.genders || [],
      ethnicities: c.ethnicities || [],
      phototypes: c.phototypes || [],
      staff_only: !!c.staff_only,
      no_active_studies: !!c.no_active_studies,
      dg_include: c.disease_groups_include || [],
      dg_exclude: c.disease_groups_exclude || [],
    });
    const qf = {}, qm = {};
    Object.entries(c.qsalud_filters || {}).forEach(([fk, spec]) => {
      if (Array.isArray(spec)) { qf[fk] = spec; qm[fk] = 'or'; }
      else if (spec && typeof spec === 'object') { qf[fk] = spec.values || []; qm[fk] = spec.mode || 'or'; }
    });
    setQsaludFilters(qf);
    setQsaludMode(qm);
    setQsaludText({ ...(c.qsalud_text_search || {}) });
    setActiveSegment({ id: seg.id, name: seg.name, description: seg.description || '' });
    setShowSegments(false);
    // Relanzar la busqueda con los criterios del segmento (sin esperar al estado).
    setLoading(true);
    setSearched(true);
    try {
      const r = await axios.post(`${API_URL}/api/recruitment/search`, c, { withCredentials: true });
      setResults(r.data.data || []);
      setTotal(r.data.total || 0);
      toast.success(lb.segmentLoaded);
    } catch (err) {
      toast.error(errText(err));
    }
    setLoading(false);
  };


  const handleReset = () => {
    setFilters({ ...INITIAL_FILTERS });
    setQsaludFilters({ ...INITIAL_QSALUD_FILTERS });
    setQsaludMode({ ...INITIAL_QSALUD_MODE });
    setQsaludText({ ...INITIAL_QSALUD_TEXT });
    setResults(null);
    setTotal(0);
    setSearched(false);
    setActiveSegment(null);
  };

  const handleGenerateList = async () => {
    if (!listName.trim()) { toast.error(lang === 'es' ? 'Nombre obligatorio' : 'Name required'); return; }
    setGeneratingList(true);
    setGenProgress('');
    try {
      const subscribers = validEmails.map(v => ({ email: v.email, name: v.display_name }));
      const skipped = results.length - subscribers.length;
      const chunks = [];
      for (let i = 0; i < subscribers.length; i += 500) {
        chunks.push(subscribers.slice(i, i + 500));
      }
      const totalLists = chunks.length;
      let created = 0;

      for (let i = 0; i < totalLists; i++) {
        const name = totalLists === 1 ? listName.trim() : `${listName.trim()} (${i + 1}/${totalLists})`;
        setGenProgress(lang === 'es'
          ? `Creando lista ${i + 1} de ${totalLists}...`
          : `Creating list ${i + 1} of ${totalLists}...`);

        if (i > 0) await new Promise(r => setTimeout(r, 3000));

        const createRes = await axios.post(`${API_URL}/api/acumbamail/lists`, {
          name, company: 'Laboratorio Goya Innova',
        }, { withCredentials: true });
        const newListId = typeof createRes.data === 'object' ? (createRes.data.id || createRes.data) : createRes.data;

        // Send in sub-batches of 50 from frontend to avoid proxy timeout
        const chunk = chunks[i];
        for (let j = 0; j < chunk.length; j += 50) {
          const subBatch = chunk.slice(j, j + 50);
          setGenProgress(lang === 'es'
            ? `Lista ${i + 1}/${totalLists}: anadiendo ${j + subBatch.length} de ${chunk.length} suscriptores...`
            : `List ${i + 1}/${totalLists}: adding ${j + subBatch.length} of ${chunk.length} subscribers...`);
          await axios.post(`${API_URL}/api/acumbamail/lists/${newListId}/subscribers/bulk`, { subscribers: subBatch }, { withCredentials: true });
          if (j + 50 < chunk.length) await new Promise(r => setTimeout(r, 1500));
        }
        created++;
      }

      toast.success(lang === 'es'
        ? `${created} lista${created > 1 ? 's' : ''} creada${created > 1 ? 's' : ''}: ${subscribers.length} suscriptores unicos, ${skipped} duplicados/sin email`
        : `${created} list${created > 1 ? 's' : ''} created: ${subscribers.length} unique subscribers, ${skipped} duplicates/no email`);
      setShowGenerateList(false);
      setListName('');
      setGenProgress('');
    } catch (err) {
      toast.error(errText(err));
    }
    setGeneratingList(false);
  };

  const handleSendSms = async () => {
    if (!smsBody.trim()) { toast.error(lang === 'es' ? 'Mensaje obligatorio' : 'Message required'); return; }
    const volIds = results.filter(v => v.phone && v.phone.trim()).map(v => v.volunteer_id);
    if (!volIds.length) { toast.error(lang === 'es' ? 'Ningun voluntario tiene telefono' : 'No volunteer has a phone'); return; }
    setSendingSms(true);
    try {
      const r = await axios.post(`${API_URL}/api/esendex/send-to-volunteers`, { volunteer_ids: volIds, body: smsBody }, { withCredentials: true });
      let msg = `${r.data.sent} ${lb.smsSent}, ${r.data.skipped} ${lb.skippedNoPhone}`;
      if (r.data.skipped_inactive) {
        msg += `, ${r.data.skipped_inactive} ${lang === 'es' ? 'omitidos inactivos' : 'skipped (inactive)'}`;
      }
      toast.success(msg);
      setShowSendSms(false);
      setSmsBody('');
    } catch (err) { toast.error(errText(err)); }
    setSendingSms(false);
  };

  return (
    <div className="space-y-6" data-testid="recruitment-page">
      <div className="flex items-center gap-3">
        <UsersFour size={28} weight="duotone" className="text-[#1f3e80]" />
        <h1 className="text-3xl font-heading font-semibold tracking-tight text-stone-900">{lb.title}</h1>
      </div>

      <DataCoverageDashboard lang={lang} />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Filters panel */}
        <div className="xl:col-span-1 space-y-4">
          {/* Personal data */}
          <Card className="border-stone-200 rounded-sm shadow-none">
            <CardContent className="p-4 space-y-4">
              <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider">{lb.personalData}</p>

              {/* Age */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">{lb.age}</Label>
                <div className="flex items-center gap-2">
                  <Input data-testid="recruit-age-from" type="number" min="0" max="120" placeholder={lb.from}
                    value={filters.age_from} onChange={e => setFilters(p => ({ ...p, age_from: e.target.value }))}
                    className="h-8 text-xs w-24" />
                  <span className="text-xs text-stone-400">—</span>
                  <Input data-testid="recruit-age-to" type="number" min="0" max="120" placeholder={lb.to}
                    value={filters.age_to} onChange={e => setFilters(p => ({ ...p, age_to: e.target.value }))}
                    className="h-8 text-xs w-24" />
                </div>
              </div>

              {/* Gender */}
              <ChipGroup label={lb.gender} field="genders" options={OPTIONS.genders} filters={filters} toggleOption={toggleOption} />

              {/* Ethnicity */}
              <ChipGroup label={lb.ethnicity} field="ethnicities" options={OPTIONS.ethnicities} filters={filters} toggleOption={toggleOption} />

              {/* Staff */}
              <div className="space-y-1.5 flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="recruit-staff-toggle">
                  <Switch checked={filters.staff_only} onCheckedChange={v => setFilters(p => ({ ...p, staff_only: v }))} className="scale-90" />
                  <span className="text-xs font-medium text-stone-700">{lb.staffOnly}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="recruit-no-active-studies-toggle">
                  <Switch checked={filters.no_active_studies} onCheckedChange={v => setFilters(p => ({ ...p, no_active_studies: v }))} className="scale-90" />
                  <span className="text-xs font-medium text-stone-700">{lb.noActiveStudies}</span>
                </label>
              </div>
            </CardContent>
          </Card>

          {/* URS-F456: Qsalud (dinamico) */}
          <Card className="border-stone-200 rounded-sm shadow-none">
            <CardContent className="p-4 space-y-4">
              <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider">{lb.qsalud}</p>

              {/* Chips dinamicos: multiselect / select / radio / checkbox */}
              {qsaludChipFields.length === 0 && (
                <p className="text-[11px] text-stone-400 italic">
                  {lang === 'es'
                    ? 'Cargando campos del Qsalud…'
                    : 'Loading Qsalud fields…'}
                </p>
              )}
              {qsaludChipFields.map(f => {
                const label = lang === 'es' ? f.label_es : f.label_en;
                const opts = f.options || [];
                const values = qsaludFilters[f.field_key] || [];
                const isMulti = f.array === true;
                const modeSel = qsaludMode[f.field_key] || 'or';
                const hasMulti = isMulti && values.length > 1;
                return (
                  <div key={f.field_key} className="space-y-1.5" data-testid={`recruit-qsalud-field-${f.field_key}`}>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-medium">{label}</Label>
                      {hasMulti && (
                        <div className="flex items-center gap-1.5">
                          <span className={`text-[10px] ${modeSel === 'or' ? 'font-semibold text-[#1f3e80]' : 'text-stone-400'}`}>{lb.any}</span>
                          <Switch
                            data-testid={`recruit-qsalud-${f.field_key}-mode`}
                            checked={modeSel === 'and'}
                            onCheckedChange={v => setQsaludMode(p => ({ ...p, [f.field_key]: v ? 'and' : 'or' }))}
                            className="h-4 w-7"
                          />
                          <span className={`text-[10px] ${modeSel === 'and' ? 'font-semibold text-[#1f3e80]' : 'text-stone-400'}`}>{lb.all}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {opts.map(opt => {
                        const selected = values.includes(opt);
                        return (
                          <button key={opt} type="button"
                            data-testid={`recruit-qsalud-${f.field_key}-${opt}`}
                            onClick={() => toggleQsalud(f.field_key, opt)}
                            className={`px-2.5 py-1 rounded text-[11px] border transition-colors ${
                              selected
                                ? 'bg-[#1f3e80] text-white border-[#1f3e80]'
                                : 'bg-white text-stone-600 border-stone-200 hover:border-stone-300 hover:bg-stone-50'
                            }`}>
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              <div className="pt-3 border-t border-stone-200" data-testid="recruit-disease-groups">
                <DiseaseGroupFilter lang={lang} include={filters.dg_include} exclude={filters.dg_exclude} testidPrefix="recruit-dg"
                  onChange={(inc, exc) => setFilters(p => ({ ...p, dg_include: inc, dg_exclude: exc }))} />
              </div>

              {/* Busqueda libre en textareas (enfermedades_actuales, alergias, observaciones...) */}
              {qsaludTextFields.length > 0 && (
                <div className="pt-3 border-t border-stone-200 space-y-2">
                  <p className="text-[10px] uppercase font-semibold text-stone-400 tracking-wider">
                    {lang === 'es' ? 'Busqueda en texto libre' : 'Free text search'}
                  </p>
                  {qsaludTextFields.map(f => (
                    <div key={f.field_key} className="space-y-1" data-testid={`recruit-qsalud-text-${f.field_key}`}>
                      <Label className="text-xs font-medium text-stone-600">
                        {lang === 'es' ? f.label_es : f.label_en}
                      </Label>
                      <Input
                        data-testid={`recruit-qsalud-text-input-${f.field_key}`}
                        placeholder={lang === 'es' ? 'Contiene…' : 'Contains…'}
                        value={qsaludText[f.field_key] || ''}
                        onChange={e => setQsaludText(p => ({ ...p, [f.field_key]: e.target.value }))}
                        className="h-8 text-xs"
                      />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Buttons */}
          <div className="flex gap-2">
            <Button onClick={handleSearch} disabled={loading} className="flex-1 bg-[#1f3e80] hover:bg-[#2a4f8a] text-white gap-2" data-testid="recruit-search-btn">
              {loading ? <ArrowsClockwise size={16} className="animate-spin" /> : <MagnifyingGlass size={16} />}
              {loading ? lb.searching : lb.search}
            </Button>
            <Button variant="outline" onClick={handleReset} className="gap-2" data-testid="recruit-reset-btn">
              <X size={14} /> {lb.reset}
            </Button>
          </div>
          <Button variant="outline" onClick={openSegments} className="w-full mt-2 gap-2 border-[#1f3e80]/30 text-[#1f3e80] hover:bg-[#1f3e80]/5" data-testid="recruit-segments-btn">
            <UsersThree size={16} /> {lb.segments}
          </Button>
        </div>

        {/* Results panel */}
        <div className="xl:col-span-2">
          {searched && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Funnel size={16} className="text-[#1f3e80]" />
                  <span className="text-sm font-semibold text-stone-800">{lb.results}</span>
                  <Badge variant="outline" className="text-xs font-mono">{total} {lb.volunteersFound}</Badge>
                  {activeSegment && (
                    <Badge variant="outline" className="text-xs border-violet-300 text-violet-700 bg-violet-50 gap-1" data-testid="recruit-editing-segment">
                      <UsersThree size={12} /> {lb.editingSegment}: {activeSegment.name}
                    </Badge>
                  )}
                </div>
                {results && results.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Button size="sm" onClick={openSaveSegment} className="gap-1.5 text-xs bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-50" disabled={Object.keys(buildSearchPayload()).length === 0} title={Object.keys(buildSearchPayload()).length === 0 ? (lang === 'es' ? 'Aplica algún filtro para guardar un segmento' : 'Apply a filter to save a segment') : ''} data-testid="recruit-save-segment-btn">
                      <FloppyDisk size={14} /> {lb.saveSegment}
                    </Button>
                    <Button size="sm" onClick={() => setShowGenerateList(true)} className="gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="recruit-generate-list-btn">
                      <ListPlus size={14} /> {lb.generateList}
                    </Button>
                    <Button size="sm" onClick={handleExportExcel} disabled={exportingExcel} className="gap-1.5 text-xs bg-[#1f3e80] hover:bg-[#2a4f8a] text-white" data-testid="recruit-export-excel-btn">
                      <FileXls size={14} /> {exportingExcel ? (lang === 'es' ? 'Generando...' : 'Generating...') : lb.generateExcel}
                    </Button>
                  </div>
                )}
              </div>
              <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
                {loading ? (
                  <div className="p-12 text-center"><p className="text-sm text-stone-400">{lb.searching}</p></div>
                ) : results && results.length === 0 ? (
                  <div className="p-12 text-center">
                    <UsersFour size={40} weight="duotone" className="text-stone-300 mx-auto mb-3" />
                    <p className="text-sm text-stone-500">{lb.noResults}</p>
                  </div>
                ) : results && (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{lb.id}</TableHead>
                        <TableHead>{lb.name}</TableHead>
                        <TableHead>{lb.email}</TableHead>
                        <TableHead>{lb.phone}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {results.map(v => (
                        <TableRow key={v.volunteer_id} data-testid={`recruit-result-${v.volunteer_id}`} className="hover:bg-stone-50 cursor-pointer" onClick={() => navigate(`/volunteers/${v.volunteer_id}`)}>
                          <TableCell className="font-mono text-xs text-stone-600">{v.volunteer_id}</TableCell>
                          <TableCell className="text-sm font-medium text-stone-900">{v.display_name}</TableCell>
                          <TableCell className="text-sm text-stone-600">{v.email || '-'}</TableCell>
                          <TableCell className="text-sm text-stone-600">{v.phone || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </div>
          )}
          {!searched && (
            <div className="flex items-center justify-center h-64 text-stone-400">
              <div className="text-center">
                <Funnel size={48} weight="duotone" className="mx-auto mb-3 text-stone-300" />
                <p className="text-sm">{lang === 'es' ? 'Configura los filtros y pulsa Buscar' : 'Set filters and press Search'}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Generate List Dialog */}
      <Dialog open={showGenerateList} onOpenChange={() => { if (!generatingList) setShowGenerateList(false); }}>
        <DialogContent className="sm:max-w-md" data-testid="generate-list-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">{lb.generateListTitle}</DialogTitle>
            <DialogDescription className="text-xs text-stone-500">{lb.generateListDesc}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">{lb.listName} *</Label>
              <Input data-testid="generate-list-name" value={listName} onChange={e => setListName(e.target.value)}
                placeholder={lang === 'es' ? 'Ej: Mujeres 30-50 Piel Seca' : 'E.g.: Women 30-50 Dry Skin'} />
            </div>
            <div className="bg-stone-50 border border-stone-200 rounded p-2.5 text-xs text-stone-600 space-y-1">
              <p>{validEmails.length} emails {lang === 'es' ? 'validos de' : 'valid from'} {results ? results.length : 0} {lang === 'es' ? 'voluntarios' : 'volunteers'}</p>
              {numLists > 1 && (
                <p className="font-semibold text-[#1f3e80]">
                  {lang === 'es'
                    ? `Se crearan ${numLists} listas de max. 500 suscriptores: "${listName || '...'} (1/${numLists})", "${listName || '...'} (2/${numLists})"...`
                    : `${numLists} lists of max. 500 subscribers will be created: "${listName || '...'} (1/${numLists})", "${listName || '...'} (2/${numLists})"...`}
                </p>
              )}
            </div>
            {genProgress && (
              <div className="bg-[#1f3e80]/5 border border-[#1f3e80]/20 rounded p-2.5 text-xs text-[#1f3e80] flex items-center gap-2">
                <ArrowsClockwise size={12} className="animate-spin" /> {genProgress}
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowGenerateList(false)} disabled={generatingList} className="text-xs">
              {lang === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button onClick={handleGenerateList} disabled={generatingList} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs gap-1.5" data-testid="confirm-generate-list">
              <ListPlus size={14} />
              {generatingList ? lb.creating : lb.generateList}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send SMS Dialog */}
      <Dialog open={showSendSms} onOpenChange={() => { if (!sendingSms) setShowSendSms(false); }}>
        <DialogContent className="sm:max-w-md" data-testid="send-sms-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">{lb.sendSmsTitle}</DialogTitle>
            <DialogDescription className="text-xs text-stone-500">{lb.sendSmsDesc}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-stone-50 border border-stone-200 rounded p-2.5 text-xs text-stone-600">
              {results ? results.filter(v => v.phone && v.phone.trim()).length : 0} {lang === 'es' ? 'voluntarios con telefono de' : 'volunteers with phone from'} {results ? results.length : 0} {lang === 'es' ? 'total' : 'total'}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{lb.smsBody} *</Label>
              <textarea data-testid="recruit-sms-body" value={smsBody} onChange={e => setSmsBody(e.target.value)}
                className="w-full min-h-[100px] text-xs border border-stone-200 rounded p-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                placeholder={lang === 'es' ? 'Escribe tu mensaje...' : 'Write your message...'} />
              <p className="text-[10px] text-stone-400">{smsBody.length}/160 {lang === 'es' ? 'caracteres' : 'characters'} ({Math.ceil((smsBody.length || 1) / 160)} SMS {lang === 'es' ? 'por destinatario' : 'per recipient'})</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowSendSms(false)} disabled={sendingSms} className="text-xs">
              {lang === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button onClick={handleSendSms} disabled={sendingSms} className="bg-amber-600 hover:bg-amber-700 text-white text-xs gap-1.5" data-testid="confirm-send-sms">
              <DeviceMobile size={14} />
              {sendingSms ? (lang === 'es' ? 'Enviando...' : 'Sending...') : lb.sendSms}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save Segment Dialog */}
      <Dialog open={showSaveSegment} onOpenChange={() => { if (!savingSegment) setShowSaveSegment(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2"><UsersThree size={18} className="text-violet-600" /> {lb.saveSegmentTitle}</DialogTitle>
            <DialogDescription className="text-xs">{lb.saveSegmentDesc}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <Label className="text-xs">{lb.segmentName}</Label>
              <Input value={segmentName} onChange={(e) => setSegmentName(e.target.value)} data-testid="segment-name-input" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">{lb.segmentDesc}</Label>
              <Input value={segmentDescription} onChange={(e) => setSegmentDescription(e.target.value)} data-testid="segment-desc-input" className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveSegment(false)} disabled={savingSegment} className="text-xs">{lb.reset === 'Reset' ? 'Cancel' : 'Cancelar'}</Button>
            {activeSegment && (
              <Button onClick={handleUpdateSegment} disabled={savingSegment} className="bg-violet-600 hover:bg-violet-700 text-white text-xs gap-1.5" data-testid="confirm-update-segment">
                <FloppyDisk size={14} /> {savingSegment ? lb.creating : `${lb.updateSegment}`}
              </Button>
            )}
            <Button onClick={handleSaveSegment} disabled={savingSegment} variant={activeSegment ? 'outline' : 'default'} className={activeSegment ? 'text-xs gap-1.5 border-violet-300 text-violet-700' : 'bg-violet-600 hover:bg-violet-700 text-white text-xs gap-1.5'} data-testid="confirm-save-segment">
              <Plus size={14} /> {savingSegment ? lb.creating : (activeSegment ? lb.saveAsNew : lb.save)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Segments Manager Dialog */}
      <Dialog open={showSegments} onOpenChange={setShowSegments}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2"><UsersThree size={18} className="text-[#1f3e80]" /> {lb.segmentsTitle}</DialogTitle>
            <DialogDescription className="text-xs">{lb.saveSegmentDesc}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-1 max-h-[55vh] overflow-y-auto" data-testid="segments-list">
            {loadingSegments ? (
              <p className="text-sm text-stone-400 text-center py-6">{lb.searching}</p>
            ) : segmentsList.length === 0 ? (
              <p className="text-sm text-stone-500 text-center py-6">{lb.noSegments}</p>
            ) : segmentsList.map((s) => (
              <div key={s.id} className="flex items-start justify-between gap-2 border border-stone-200 rounded-sm p-2.5" data-testid={`segment-row-${s.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-stone-800 truncate">{s.name}</p>
                  {s.description && <p className="text-xs text-stone-500 truncate">{s.description}</p>}
                  {s.created_by && <p className="text-[11px] text-stone-400 mt-0.5">{s.created_by}</p>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="outline" size="sm" onClick={() => loadSegment(s)} data-testid={`segment-use-${s.id}`} className="h-7 px-2 text-xs text-[#1f3e80] border-[#1f3e80]/30 hover:bg-[#1f3e80]/5 gap-1">
                    <MagnifyingGlass size={13} /> {lb.useSegment}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteSegment(s.id)} data-testid={`segment-delete-${s.id}`} className="h-7 px-2 text-red-600 hover:bg-red-50 gap-1">
                    <Trash size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}


function ChipGroup({ label, field, options, filters, toggleOption }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium">{label}</Label>
      <div className="flex flex-wrap gap-1.5">
        {options.map(opt => {
          const selected = filters[field].includes(opt);
          return (
            <button key={opt} type="button" data-testid={`recruit-${field}-${opt}`}
              onClick={() => toggleOption(field, opt)}
              className={`px-2.5 py-1 rounded text-[11px] border transition-colors ${
                selected
                  ? 'bg-[#1f3e80] text-white border-[#1f3e80]'
                  : 'bg-white text-stone-600 border-stone-200 hover:border-stone-300 hover:bg-stone-50'
              }`}>
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}


function ChipGroupWithMode({ label, field, options, filters, toggleOption, setFilters, lb }) {
  const mode = filters[`${field}_mode`] || 'or';
  const hasSelection = filters[field].length > 1;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-medium">{label}</Label>
        {hasSelection && (
          <div className="flex items-center gap-1.5">
            <span className={`text-[10px] ${mode === 'or' ? 'font-semibold text-[#1f3e80]' : 'text-stone-400'}`}>{lb.any}</span>
            <Switch
              data-testid={`recruit-${field}-mode`}
              checked={mode === 'and'}
              onCheckedChange={v => setFilters(p => ({ ...p, [`${field}_mode`]: v ? 'and' : 'or' }))}
              className="h-4 w-7"
            />
            <span className={`text-[10px] ${mode === 'and' ? 'font-semibold text-[#1f3e80]' : 'text-stone-400'}`}>{lb.all}</span>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {options.map(opt => {
          const selected = filters[field].includes(opt);
          return (
            <button key={opt} type="button" data-testid={`recruit-${field}-${opt}`}
              onClick={() => toggleOption(field, opt)}
              className={`px-2.5 py-1 rounded text-[11px] border transition-colors ${
                selected
                  ? 'bg-[#1f3e80] text-white border-[#1f3e80]'
                  : 'bg-white text-stone-600 border-stone-200 hover:border-stone-300 hover:bg-stone-50'
              }`}>
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}


// URS-F185 + URS-F488c: Data coverage dashboard - shows the % of active volunteers with
// each Qsalud + Personal Data field populated. Helps the recruiter understand
// data reliability BEFORE running an eligibility search.
// URS-F488c: refetch automatico al volver a poner el foco a la pestana
// (window focus + visibilitychange) + boton manual de refrescar +
// timestamp de ultima actualizacion para transparencia.
function DataCoverageDashboard({ lang }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchCoverage = useCallback(() => {
    setLoading(true);
    return axios.get(`${API_URL}/api/recruitment/data-coverage`, { withCredentials: true })
      .then(({ data }) => {
        setData(data);
        setLastRefresh(new Date());
      })
      .catch(() => toast.error(lang === 'es' ? 'Error al cargar cobertura' : 'Error loading coverage'))
      .finally(() => setLoading(false));
  }, [lang]);

  useEffect(() => {
    fetchCoverage();
  }, [fetchCoverage]);

  // URS-F488c: refetch al re-enfocar la pestana. React no re-monta el
  // componente si el usuario navega en otro tab del navegador, edita
  // voluntarios y vuelve; sin esto, la cifra se queda "congelada".
  useEffect(() => {
    const onFocus = () => { if (document.visibilityState === 'visible') fetchCoverage(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [fetchCoverage]);

  const T = lang === 'es' ? {
    title: 'Cobertura de datos',
    subtitle: 'Porcentaje de voluntarios activos con cada campo del Qsalud / Datos personales rellenado',
    activeTotal: 'voluntarios activos',
    reclutables: 'reclutables',
    inStudy: 'en estudio',
    totalActive: 'total',
    personal: 'Datos personales',
    qsalud: 'Qsalud',
    loading: 'Cargando cobertura...',
    of: 'de',
    refresh: 'Refrescar',
    updated: 'Actualizado',
  } : {
    title: 'Data coverage', subtitle: 'Percentage of active volunteers with each Qsalud / Personal Data field populated',
    activeTotal: 'active volunteers', reclutables: 'available', inStudy: 'in study', totalActive: 'total',
    personal: 'Personal data', qsalud: 'Qsalud', loading: 'Loading coverage...',
    of: 'of', refresh: 'Refresh', updated: 'Updated',
  };

  const getColor = (pct) => {
    if (pct >= 80) return { bar: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200' };
    if (pct >= 50) return { bar: 'bg-amber-500', text: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' };
    if (pct >= 20) return { bar: 'bg-orange-500', text: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-200' };
    return { bar: 'bg-red-500', text: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200' };
  };

  if (loading || !data) {
    return (
      <Card className="border-stone-200 rounded-sm shadow-none" data-testid="coverage-dashboard">
        <CardContent className="p-4 text-xs text-stone-400 text-center">{T.loading}</CardContent>
      </Card>
    );
  }

  const personalFields = data.fields.filter(f => f.source === 'personal');
  const qsaludFields = data.fields.filter(f => f.source === 'qsalud');

  const renderField = (f) => {
    const c = getColor(f.percentage);
    const label = lang === 'es' ? f.label_es : f.label_en;
    return (
      <div key={f.field_path} className={`border rounded-sm p-2.5 ${c.border} ${c.bg}`} data-testid={`coverage-field-${f.field_key}`}>
        <div className="flex items-baseline justify-between gap-2 mb-1.5">
          <span className="text-xs font-medium text-stone-800 truncate" title={label}>{label}</span>
          <span className={`text-sm font-bold ${c.text} shrink-0`}>{f.percentage}%</span>
        </div>
        <div className="w-full h-1.5 bg-white rounded-full overflow-hidden">
          <div className={`h-full ${c.bar} transition-all`} style={{ width: `${f.percentage}%` }} />
        </div>
        <p className="text-[10px] text-stone-500 mt-1 font-mono">{f.filled.toLocaleString()} {T.of} {f.total.toLocaleString()}</p>
      </div>
    );
  };

  return (
    <Card className="border-stone-200 rounded-sm shadow-none" data-testid="coverage-dashboard">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <button
            type="button"
            className="flex items-center gap-2 text-left hover:opacity-80"
            onClick={() => setExpanded(e => !e)}
            data-testid="coverage-toggle-btn"
          >
            <ChartBar size={20} weight="duotone" className="text-[#1f3e80]" />
            <div>
              <p className="text-sm font-semibold text-stone-800 flex items-center gap-2">
                {T.title}
                {expanded ? <CaretUp size={14} className="text-stone-400" /> : <CaretDown size={14} className="text-stone-400" />}
              </p>
              <p className="text-[11px] text-stone-500">{T.subtitle}</p>
            </div>
          </button>
          <div className="flex items-center gap-2">
            {lastRefresh && (
              <span className="text-[10px] text-stone-400 font-mono hidden sm:inline" title={lastRefresh.toISOString()} data-testid="coverage-last-refresh">
                {T.updated} {lastRefresh.toLocaleTimeString(lang === 'es' ? 'es-ES' : 'en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </span>
            )}
            <button
              type="button"
              onClick={fetchCoverage}
              disabled={loading}
              className="h-7 w-7 flex items-center justify-center rounded-sm text-stone-500 hover:text-[#1f3e80] hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title={T.refresh}
              data-testid="coverage-refresh-btn"
            >
              <ArrowsClockwise size={14} className={loading ? 'animate-spin' : ''} />
            </button>
            <Badge variant="outline" className="text-xs font-mono flex-col items-end !py-1 !px-2 leading-tight" data-testid="coverage-total-active" title={`${data.active_strict} ${T.reclutables} + ${data.in_study} ${T.inStudy} = ${data.total_active} ${T.totalActive}`}>
              <span className="whitespace-nowrap">
                <span className="text-emerald-700 font-semibold">{(data.active_strict || 0).toLocaleString()}</span>
                <span className="text-stone-500 ml-1">{T.reclutables}</span>
              </span>
              <span className="text-[10px] text-stone-500 whitespace-nowrap" data-testid="coverage-in-study">
                + <span className="text-amber-700 font-semibold">{(data.in_study || 0).toLocaleString()}</span> {T.inStudy}
                <span className="text-stone-400"> · {data.total_active.toLocaleString()} {T.totalActive}</span>
              </span>
            </Badge>
          </div>
        </div>
        {expanded && (
          <div className="space-y-3">
            <div>
              <p className="text-[10px] uppercase font-semibold text-emerald-700 tracking-wide mb-1.5">{T.personal}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                {personalFields.map(renderField)}
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase font-semibold text-[#1f3e80] tracking-wide mb-1.5">{T.qsalud}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                {qsaludFields.map(renderField)}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
